import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import classNames from 'classnames'
import Dropzone from 'react-dropzone'
import { IImage } from '@utils/contentful/contentfulImage'
import { FileUploadProgressBar } from '../FileUploadProgressBar'
import { progressBarStateEnum } from '@components/common/ProgressBar'
import {
  canFileBeConvertedToSvg,
  fileFormatValidator,
  getFileExtension,
  getSvgData,
  logUploadErrorWithSentry,
} from './UploadDropzone.utils'
import { Modal } from '@noissue-ui-kit/modal'
import { FileURL } from '../../types'
import { ContentfulMarkdown } from '@utils/contentful/ContentfulMarkdown'
import { useFilesWithState, useSelectedFile } from './UploadDropzone.provider'

interface DropzoneContent {
  title: string
  subtitle: string
  footer: string
  icon: IImage
}

export enum UploaderState {
  IDLE = 'idle',
  HOVER = 'hover',
  UPLOADING = 'uploading',
  SUCCESS = 'success',
  ERROR = 'error',
}

export enum ImageConversionState {
  NOT_CONVERTED = 'not-converted',
  CONVERSION_ERROR = 'conversion-error',
  CONVERSION_SUCCESS = 'conversion-success',
}

export type TFileWithState = {
  file: File
  fileAsSVG?: string
  fileAsDataUrl?: string
  fileConversionState: ImageConversionState
  state: UploaderState
  uploadingPercentage: number
  message?: string
  uploadedFileUrl?: FileURL
  metaData?: {
    format: string
    height: number
    width: number
  }
}

export function UploadDropzone({
  idleStateContent,
  hoverStateContent,
  successStateContent,
  upload,
  removeUploadFile,
  className,
  trackUploadedFile,
  selectable,
  preselectedFileName,
  disableUpload = false,
  uploadType,
}: {
  idleStateContent: DropzoneContent
  hoverStateContent: DropzoneContent
  successStateContent: DropzoneContent
  upload: (fileUrls: FileURL[]) => void
  removeUploadFile: (fileUrl: FileURL) => void
  className?: string
  trackUploadedFile?: ({ fileUrl }) => void
  selectable: boolean
  preselectedFileName?: string
  disableUpload?: boolean
  uploadType: 'upload' | 'svg-convert'
}) {
  const { filesWithState, setFilesWithState } = useFilesWithState()
  const [openRemoveFileModal, setOpenRemoveFileModal] = useState<boolean>(false)
  const [confirmDeleteFile, setConfirmDeleteFile] = useState<TFileWithState>()
  const [isHover, setIsHover] = useState(false)
  const [uploadingPostsMap, setUploadingPostsMap] = useState(new WeakMap())

  const foundSuccessUpload = filesWithState.find(
    (fileWithState) => fileWithState.state === UploaderState.SUCCESS
  )
  const showSuccessText = filesWithState.find(
    (fileWithState) => fileWithState.state !== UploaderState.IDLE
  )
  const { selectedFile, setSelectedFile } = useSelectedFile()
  const uploadAreaContent = isHover ? hoverStateContent : idleStateContent

  const handleRemoveUploadFile = (file: TFileWithState) => {
    setOpenRemoveFileModal(true)
    setConfirmDeleteFile(file)
  }

  const onDrop = async (acceptedFiles) => {
    setIsHover(false)
    const acceptedFilesWithState: TFileWithState[] = []
    for (const acceptedFile of acceptedFiles) {
      acceptedFilesWithState.push({
        file: acceptedFile,
        state: UploaderState.UPLOADING,
        uploadingPercentage: 0,
        fileConversionState: ImageConversionState.NOT_CONVERTED,
      })
    }
    setFilesWithState((filesWithState) => [
      ...acceptedFilesWithState,
      ...filesWithState,
    ])

    for (const file of acceptedFiles) {
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('key', file.name)

        const CancelToken = axios.CancelToken
        const source = CancelToken.source()
        setUploadingPostsMap((uploadingPostsMap) => {
          uploadingPostsMap.set(file, source)
          return uploadingPostsMap
        })
        const { data } = await axios.post('/api/magento/uploadpdf/', formData, {
          cancelToken: source.token,
          onUploadProgress: function (progressEvent: ProgressEvent) {
            const percentCompleted = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            )
            setFilesWithState((filesWithState) =>
              filesWithState.map((fileWithState) => {
                if (fileWithState.file === file) {
                  fileWithState.uploadingPercentage = percentCompleted
                }
                return fileWithState
              })
            )
          },
        })

        const uploadedUrl = data?.fileUrl
        const metaData = data?.metaData

        trackUploadedFile({ fileUrl: uploadedUrl })

        upload([uploadedUrl])
        ;((file, uploadedUrl) => {
          setFilesWithState((filesWithState) =>
            filesWithState.map((fileWithState) => {
              if (fileWithState.file === file) {
                fileWithState.uploadingPercentage = 100
                fileWithState.state = UploaderState.SUCCESS
                fileWithState.uploadedFileUrl = uploadedUrl
                fileWithState.metaData = {
                  format: metaData?.format,
                  height: metaData?.height,
                  width: metaData?.width,
                }
              }
              return fileWithState
            })
          )
        })(file, uploadedUrl)
      } catch (error) {
        logUploadErrorWithSentry(error, file)
        const errorFileWithState: TFileWithState = {
          file,
          state: UploaderState.ERROR,
          fileConversionState: ImageConversionState.NOT_CONVERTED,
          uploadingPercentage: 100,
          message: error.message,
        }
        setFilesWithState((filesWithState) =>
          filesWithState.map((fileWithState) =>
            fileWithState.file === errorFileWithState.file
              ? errorFileWithState
              : fileWithState
          )
        )
      }
    }
  }
  const getDataUrl = async (file: File): Promise<{ dataUrl: string }> => {
    const dataUrl: any = await new Promise((resolve) => {
      let reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.readAsDataURL(file)
    })

    return {
      dataUrl,
    }
  }

  const convertFileToSvgAndSaveToState = useCallback(
    async (fileWithState: TFileWithState) => {
      try {
        let svgData = ''
        let dataUrl = ''

        if (!fileWithState?.file?.size && fileWithState?.state === 'success') {
          const extension = getFileExtension(fileWithState?.file)
          const fileResponse = await fetch(fileWithState.uploadedFileUrl)
          const fileBuffer = await fileResponse.blob()
          const file = new File([fileBuffer], fileWithState?.file?.name)

          svgData =
            extension.toLowerCase() === 'svg'
              ? await fileResponse.text()
              : (await getSvgData(file))?.svgData
          dataUrl = (await getDataUrl(file))?.dataUrl
        } else {
          svgData = fileWithState?.fileAsSVG
            ? fileWithState.fileAsSVG
            : (await getSvgData(fileWithState?.file))?.svgData

          dataUrl = fileWithState?.fileAsDataUrl
            ? fileWithState.fileAsDataUrl
            : (await getDataUrl(fileWithState?.file))?.dataUrl
        }

        const fileWithSvgData = {
          ...fileWithState,
          fileAsSVG: svgData,
          fileAsDataUrl: dataUrl,
          fileConversionState: ImageConversionState.CONVERSION_SUCCESS,
        } as TFileWithState

        const filesWithSvgData = filesWithState?.map((file) => {
          if (fileWithState?.file?.name === file?.file?.name) {
            return fileWithSvgData
          } else {
            return { ...file }
          }
        })

        setFilesWithState(filesWithSvgData)
        setSelectedFile(fileWithSvgData)
      } catch (error) {
        const fileWithSvgData = {
          ...fileWithState,
          fileAsSVG: '',
          fileAsDataUrl: '',
          fileConversionState: ImageConversionState.CONVERSION_ERROR,
        } as TFileWithState

        const filesWithSvgData = filesWithState?.map((file) => {
          if (fileWithState?.file?.name === file?.file?.name) {
            return fileWithSvgData
          } else {
            return { ...file }
          }
        })
        setFilesWithState(filesWithSvgData)
        setSelectedFile(fileWithSvgData)
      }
    },
    [filesWithState]
  )

  useEffect(() => {
    if (!selectedFile) {
      return
    }

    if (
      selectedFile?.fileConversionState === ImageConversionState.NOT_CONVERTED
    ) {
      convertFileToSvgAndSaveToState(selectedFile).catch(() => {})
    }
  }, [selectedFile])

  useEffect(() => {
    const preselectedFile = filesWithState.find(
      (file) => file?.file?.name === preselectedFileName
    )

    if (preselectedFile && selectable) {
      setSelectedFile(preselectedFile)
    }
  }, [preselectedFileName])

  useEffect(() => {
    if (uploadType === 'svg-convert') {
      if (filesWithState?.length > 0) {
        if (
          (selectedFile === null && !preselectedFileName) ||
          filesWithState?.findIndex(
            (fileWithState) =>
              fileWithState?.file?.name ===
              (selectedFile?.file?.name || preselectedFileName)
          ) === -1
        ) {
          setSelectedFile(filesWithState[0])
        }
      } else {
        setSelectedFile(null)
      }
    }
  }, [uploadType, filesWithState, preselectedFileName])

  useEffect(() => {
    if (
      filesWithState?.length &&
      selectedFile?.fileConversionState ===
        ImageConversionState.CONVERSION_ERROR
    ) {
      const validFile = filesWithState.find(
        (x) => x.fileConversionState !== ImageConversionState.CONVERSION_ERROR
      )
      if (validFile) {
        setSelectedFile(validFile)
      }
    }
  }, [filesWithState, selectedFile])

  return (
    <div
      className={`w-full p-0 md:p-10 border-0 md:border-4 border-black-10 rounded-3xl flex flex-col
      ${className}`}
    >
      <div
        className={`relative w-full flex flex-col justify-center flex-grow border-black-10 ${
          isHover ? 'bg-black-10' : 'bg-black-5'
        } ${disableUpload && 'opacity-50'}`}
      >
        <Dropzone
          maxSize={100_000_000}
          onDropAccepted={onDrop}
          onDropRejected={(rejectedFiles) => {
            setIsHover(false)
            const rejectedFilesWithState: TFileWithState[] = []
            for (const rejectedFile of rejectedFiles) {
              rejectedFilesWithState.push({
                file: rejectedFile.file,
                state: UploaderState.ERROR,
                message: rejectedFile.errors[0].message, // Just wanna show one of the reason
                uploadingPercentage: 100,
                fileConversionState: ImageConversionState.NOT_CONVERTED,
              })
            }
            setFilesWithState((filesWithState) => [
              ...rejectedFilesWithState,
              ...filesWithState,
            ])
          }}
          onDragOver={() => setIsHover(true)}
          onDragLeave={() => setIsHover(false)}
          validator={fileFormatValidator}
        >
          {({ getRootProps, getInputProps }) => (
            <div
              {...getRootProps({
                className: classNames(
                  'border-core-grey-40 border-dashed w-full border-4 rounded-3xl h-full absolute',
                  {
                    'pointer-events-none': disableUpload,
                  }
                ),
              })}
            >
              <input {...getInputProps()} />
            </div>
          )}
        </Dropzone>

        <div className="flex flex-col items-center justify-center px-12 py-8 pointer-events-none font-mori">
          {uploadAreaContent.title && (
            <ContentfulMarkdown overrideClass="mb-2 text-4xl text-center font-semibold text-acai select-none pointer-events-none">
              {uploadAreaContent.title}
            </ContentfulMarkdown>
          )}

          {uploadAreaContent.subtitle && (
            <ContentfulMarkdown overrideClass="mb-10 text-2xl text-center text-boulder select-none">
              {uploadAreaContent.subtitle}
            </ContentfulMarkdown>
          )}

          {uploadAreaContent.footer && (
            <ContentfulMarkdown overrideClass="text-lg text-center text-black-80 w-80 select-none">
              {uploadAreaContent.footer}
            </ContentfulMarkdown>
          )}
        </div>
      </div>

      {filesWithState.length > 0 && (
        <div className="flex flex-col mt-8 font-mori select-none">
          <div className="mb-8">
            {showSuccessText && successStateContent.title && (
              <ContentfulMarkdown overrideClass="mb-2 mr-4 text-2xl font-semibold text-acai inline-block select-none">
                {successStateContent.title}
              </ContentfulMarkdown>
            )}
            {foundSuccessUpload && successStateContent.subtitle && (
              <ContentfulMarkdown overrideClass="hidden sm:inline-block text-black-60 text-xl select-none">
                {successStateContent.subtitle}
              </ContentfulMarkdown>
            )}
          </div>

          {filesWithState.map((fileWithState, index) => (
            <FileUploadProgressBar
              key={`${fileWithState.file.name}-${index}`}
              uploadPercentage={fileWithState.uploadingPercentage}
              file={fileWithState.file}
              title={
                fileWithState.state === UploaderState.ERROR
                  ? fileWithState.message
                  : null
              }
              state={
                {
                  uploading: progressBarStateEnum.LOADING,
                  success: progressBarStateEnum.SUCCESS,
                  error: progressBarStateEnum.ERROR,
                }[fileWithState.state]
              }
              handleRemoveButtonClick={() =>
                handleRemoveUploadFile(fileWithState)
              }
              selected={
                uploadType === 'svg-convert' &&
                fileWithState?.file?.name === selectedFile?.file?.name
              }
              disabled={
                uploadType === 'svg-convert' &&
                !canFileBeConvertedToSvg(fileWithState?.file)
              }
              onSelect={
                uploadType === 'svg-convert' &&
                canFileBeConvertedToSvg(fileWithState?.file) &&
                !disableUpload
                  ? async () => {
                      setSelectedFile(fileWithState)
                    }
                  : undefined
              }
            />
          ))}
          {foundSuccessUpload && successStateContent.footer && (
            <div className="mt-4 text-lg text-core-tree-100 select-none">
              {successStateContent.footer}
            </div>
          )}
        </div>
      )}

      <Modal
        opened={openRemoveFileModal}
        size="small"
        title="Are you sure you want to remove this file?"
        confirmLabel="Yes, please remove"
        onConfirm={() => {
          setFilesWithState((filesWithState) =>
            filesWithState.filter(
              (fileWithState) => fileWithState.file !== confirmDeleteFile.file
            )
          )
          removeUploadFile(confirmDeleteFile.uploadedFileUrl)
          if (uploadingPostsMap.has(confirmDeleteFile.file)) {
            const source = uploadingPostsMap.get(confirmDeleteFile.file)
            source?.cancel()
          }
          setOpenRemoveFileModal(false)
          setConfirmDeleteFile(null)
        }}
        onCancel={() => setOpenRemoveFileModal(false)}
        className={'-translate-y-1/2'}
      />
    </div>
  )
}
